import { useMemo, useState, useEffect } from 'react';
import type { CountryItem } from './types';

export const getCompactLabel = ({ dialCode }: CountryItem) => `+${dialCode}`;

type UseCountryAutocompleteResult = {
  countryInputValue: string;
  filteredCountryItems: CountryItem[];
  handleCountrySelect: (key: React.Key | null) => void;
  handleInputChange: (val: string) => void;
  handleClose: () => void;
};

export function useCountryAutocomplete(
  countryItems: CountryItem[],
  currentIso2: string,
  setCountry: (iso2: string) => void,
): UseCountryAutocompleteResult {
  const selectedItem = useMemo(
    () => countryItems.find(({ iso2 }) => iso2 === currentIso2),
    [countryItems, currentIso2],
  );

  const compactLabel = selectedItem ? getCompactLabel(selectedItem) : '';

  const [countryInputValue, setCountryInputValue] = useState(() => compactLabel);

  // Sync trigger label when country changes externally.
  useEffect(() => {
    const found = countryItems.find(({ iso2 }) => iso2 === currentIso2);
    if (found) setCountryInputValue(getCompactLabel(found));
  }, [currentIso2, countryItems]);

  const filteredCountryItems = useMemo(() => {
    if (countryInputValue === compactLabel) return countryItems;
    const q = countryInputValue.toLowerCase().replace(/^\+/, '');
    if (!q) return countryItems;
    return countryItems.filter(({ name, dialCode }) => name.toLowerCase().includes(q) || dialCode.startsWith(q));
  }, [countryItems, countryInputValue, compactLabel]);

  const handleCountrySelect = (key: React.Key | null) => {
    if (!key) return;
    const iso2 = String(key);
    setCountry(iso2);
    const found = countryItems.find((c) => c.iso2 === iso2);
    if (found) setCountryInputValue(getCompactLabel(found));
  };

  const handleClose = () => {
    if (selectedItem) setCountryInputValue(getCompactLabel(selectedItem));
  };

  return {
    countryInputValue,
    filteredCountryItems,
    handleCountrySelect,
    handleInputChange: setCountryInputValue,
    handleClose,
  };
}
